import { default as React } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useEntity } from '@backstage/plugin-catalog-react';
import { DocsIcon, Table } from '@backstage/core-components';
import { EditManagedVpcIcon } from './ManagedVpcEditIcon';
import { useStyles } from '../styles';
import { StatusIndicatorTitle } from '../StatusIndicatorTitle';
import { Annotations, CpeCardProps, Features, Vpc } from '../types';

type RegionVpcCount = {
  [region: string]: number;
};

type WorkloadVpcDetails = {
  account_id: string;
  vpc_count: RegionVpcCount;
};

export function ManagedVpcCard(props: CpeCardProps) {
  const supportUrl =
    '/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/principal/support/#internal-support';
  const { variant } = props;
  const classes = useStyles();
  const { entity } = useEntity();

  const [vpcData] = React.useState<WorkloadVpcDetails>();

  const annotations: Annotations | undefined = entity?.metadata
    ?.annotations as Annotations;
  const accountId: string | undefined =
    annotations?.['amazonaws.com/account-id'];

  const features = entity?.spec?.features as unknown as Features;
  const vpcs: Vpc[] = features?.vpc as unknown as Vpc[];

  let cardClass = '';
  let cardContentClass = '';
  if (variant === 'gridItem') {
    cardClass = classes.gridItemCard;
    cardContentClass = classes.gridItemCardContent;
  } else if (variant === 'fullHeight') {
    cardClass = classes.fullHeightCard;
    cardContentClass = classes.fullHeightCardContent;
  }

  const EditManagedVpcIconActions = () => {
    return (
      <>
        <Tooltip title="Learn more about Foundation Managed VPC">
          <IconButton
            href="https://portal.developer.principal.com/docs/principalfinancialgroup-emu/component/principal-engineering-handbook/implementation/building/network/aws/foundation-managed-vpc/"
            target="_blank"
            style={{ marginRight: '8px' }}
          >
            <DocsIcon />
          </IconButton>
        </Tooltip>
        <EditManagedVpcIcon
          vpcs={vpcs}
          accountId={accountId!}
          vpcData={vpcData}
          cardContentClass={cardContentClass}
        />
      </>
    );
  };

  function rows(vpc_status: boolean, public_subnet_status: boolean) {
    return [
      {
        feature: 'VPC Status',
        status: vpc_status ? 'Enabled' : 'Disabled',
      },
      {
        feature: 'Public Subnet Status',
        status: public_subnet_status ? 'Enabled' : 'Disabled',
      },
    ];
  }

  const columns = [
    {
      title: 'Feature',
      field: 'metadata.annotations',
      width: '30%',
      render: (rowData: any) => {
        return rowData.feature;
      },
    },
    {
      title: 'Status',
      field: 'metadata.name',
      width: '80%',
      render: (rowData: any) => {
        return rowData.status;
      },
    },
  ];

  return (
    <Card className={cardClass}>
      <CardHeader
        title="Foundation Managed VPC"
        titleTypographyProps={{ component: 'h2' }}
        variant={variant}
        action={<EditManagedVpcIconActions />}
      />
      <Divider />
      <CardContent className={cardContentClass}>
        {vpcs &&
          vpcs.map(vpc => (
            <React.Fragment key={`${vpc.region}_allowed`}>
              <StatusIndicatorTitle
                enabled={vpc.vpc_enabled}
                title={vpc.region}
              />
              <Table
                columns={columns}
                data={rows(vpc.vpc_enabled, vpc.public_subnets_enabled)}
                options={{
                  showTitle: false,
                  loadingType: 'linear',
                  header: false,
                  search: false,
                  padding: 'dense',
                  toolbar: false,
                  paging: false,
                  pageSizeOptions: [5],
                }}
              />
              {vpcs.indexOf(vpc) < vpcs.length - 1 && (
                <Divider style={{ margin: '16px 0' }} />
              )}
            </React.Fragment>
          ))}
      </CardContent>
      <CardContent>
        <Typography variant="body1">
          To enable a region not listed above, please submit a request for{' '}
          <Link target="_blank" href={supportUrl}>
            Networking Support
          </Link>{' '}
        </Typography>
      </CardContent>
    </Card>
  );
}
